/**
 * useKeyboardScope — THE React-side entry point for keyboard chains.
 *
 * Why this hook exists
 * --------------------
 * Phase 0 of WS-NAV-5 establishes the SSOT for keyboard handling.
 * The full j/k/a/s/n/c/d/? chain (Phase 1+) consumes the Unified
 * Inbox row stream from WS-NAV-2 (IA Collapse) — not yet landed —
 * but the SCOPE MODULE itself is independent. It is the data
 * structure + the single legal global keydown listener; shipping it
 * now (a) unblocks WS-GOV-1 rule 8 (no-global-keydown ESLint rule),
 * (b) establishes the SSOT that NAV-2 + the rest of NAV-5 consume.
 *
 * Contract
 * --------
 *   const scope = useKeyboardScope([
 *     { combo: 'j',      handler: () => selectNext() },
 *     { combo: 'k',      handler: () => selectPrev() },
 *     { combo: 'mod+k',  handler: (e) => { e.preventDefault(); openPalette(); } },
 *     { combo: 'escape', handler: () => closeDrawer() },
 *   ], { owner: 'InboxList' });
 *
 *   - Pushes on mount, pops on unmount.
 *   - StrictMode double-invoke is a no-op (identity is keyed by
 *     `useId()` so the second mount of the same instance does not
 *     duplicate the scope; it just replaces the shortcut map).
 *   - Two siblings → the LATER-mounted scope wins until it unmounts;
 *     the earlier one resumes ownership.
 *   - ESC: if the consumer registers an `escape` handler, that
 *     handler runs; otherwise dispatching ESC pops the topmost
 *     scope (default "modal closes on ESC" behaviour).
 *
 * Out of scope for Phase 0
 * ------------------------
 * - Migrating existing `window.addEventListener('keydown', …)` sites
 *   (CmdK palette, etc.). That sweep lands alongside the rule 8
 *   enablement in a follow-up.
 * - The chain command grammar (j/k/a/s/n/c/d/?). NAV-2 + NAV-5
 *   Phase 1 consume this hook; the chain itself is downstream.
 *
 * see /Users/yongjie/.claude/plans/ navigation-5 R6 for the
 * stack-discipline rationale.
 */

import { useEffect, useId, useMemo, useRef } from 'react';
import {
    buildShortcutMap,
    createKeyboardScopeStack,
    type KeyComboShortcut,
    type KeyboardScope,
    type KeyboardScopeStack,
} from './keyboardScopeStack';
import { acquireGlobalKeydownListener } from './globalKeydownInstaller';

/** Hook options. `owner` is a debug label — surfaces in captureError
 *  payloads when a handler throws. */
export type UseKeyboardScopeOptions = {
    owner?: string;
};

// ── Module-singleton stack ─────────────────────────────────────────
// Lives at module scope so every hook instance pushes onto the same
// stack. Tests can reach the same singleton via the exported
// `__getKeyboardScopeStackForTests` accessor.
const moduleStack: KeyboardScopeStack = createKeyboardScopeStack();

/** Build the combo string the stack looks up from a raw event. The
 *  grammar matches `keyboardScopeStack.ts`: lowercase key with
 *  optional `mod+` / `shift+` / `alt+` / `ctrl+` prefixes joined by
 *  `+`. `mod` is meta on mac, ctrl elsewhere. */
function eventToCombo(event: KeyboardEvent): string {
    const parts: string[] = [];
    // Order matters for the canonical lookup form: mod → shift → alt → key.
    if (event.metaKey || event.ctrlKey) parts.push('mod');
    if (event.shiftKey) parts.push('shift');
    if (event.altKey) parts.push('alt');
    const key = event.key.toLowerCase();
    parts.push(key);
    return parts.join('+');
}

/** Module-singleton dispatcher passed to the installer. Resolves
 *  the topmost scope at call time so freshly-pushed scopes are
 *  reachable without re-installing the listener. */
function globalDispatcher(event: KeyboardEvent): boolean {
    // Try the canonical "mod+key" form first; fall back to the
    // bare key so consumers can register either way.
    const canonical = eventToCombo(event);
    if (moduleStack.dispatch(canonical, event)) return true;
    const bare = event.key.toLowerCase();
    if (bare !== canonical) {
        return moduleStack.dispatch(bare, event);
    }
    return false;
}

/**
 * Register a keyboard scope for the component's lifetime.
 *
 * @param shortcuts Bindings to install while this component is mounted.
 *                  Pass an empty array to register a "do-nothing" scope
 *                  that just blocks lower scopes from receiving events
 *                  (uncommon but legitimate — e.g. a focused dialog
 *                  that doesn't bind any chords).
 * @param opts      Optional debug metadata.
 */
export function useKeyboardScope(
    shortcuts: ReadonlyArray<KeyComboShortcut>,
    opts: UseKeyboardScopeOptions = {},
): void {
    // Stable identity per consumer call. StrictMode double-invokes
    // the effect with the SAME id, so the second push is a no-op
    // (see `keyboardScopeStack.push`).
    const id = useId();
    const owner = opts.owner ?? 'anonymous';

    // Re-bind the latest shortcut map on every render. We rebuild
    // the Map only when the caller-supplied array reference
    // changes — handlers are usually inlined, so the Map IS rebuilt
    // most renders, which is the desired behaviour (latest closure
    // wins without rebinding the scope identity).
    const shortcutMap = useMemo(
        () => buildShortcutMap(shortcuts),
        [shortcuts],
    );

    // Keep a ref to the latest map so the scope handle's identity
    // stays stable across re-renders. The scope sitting on the
    // stack reads `mapRef.current` indirectly via the closure below.
    const mapRef = useRef(shortcutMap);
    mapRef.current = shortcutMap;

    useEffect(() => {
        const scope: KeyboardScope = {
            id,
            owner,
            // Wrap mapRef so re-renders that swap the shortcut map
            // don't force a stack push/pop. The Map exposed to the
            // dispatcher always points at the latest closure.
            shortcuts: {
                get: (combo: string) => mapRef.current.get(combo),
                has: (combo: string) => mapRef.current.has(combo),
                // Iteration is unused by the dispatcher but required
                // to satisfy the ReadonlyMap shape; defer to the
                // ref's current value.
                get size() { return mapRef.current.size; },
                forEach: (cb) => { mapRef.current.forEach(cb); },
                entries: () => mapRef.current.entries(),
                keys: () => mapRef.current.keys(),
                values: () => mapRef.current.values(),
                [Symbol.iterator]: () => mapRef.current[Symbol.iterator](),
            } as ReadonlyMap<string, KeyComboShortcut['handler']>,
        };

        const popScope = moduleStack.push(scope);
        const releaseListener = acquireGlobalKeydownListener(globalDispatcher);

        return () => {
            popScope();
            releaseListener();
        };
    }, [id, owner]);
}

/** Test-only accessor — returns the module-singleton stack so unit
 *  tests can assert push/pop ordering across React renders without
 *  reaching into the installer. Hooks should NEVER import this. */
export function __getKeyboardScopeStackForTests(): KeyboardScopeStack {
    return moduleStack;
}
